import { useState } from 'react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { Chip } from '@/shared/ui/chip'
import { Button } from '@/shared/ui/button'
import { showToast } from '@/shared/ui/toast'
import { userRepository } from '@/features/perfil/api/user-repository'
import { logger } from '@upadelmatch/core'

/**
 * The gender wall, with a voice.
 *
 * Before this, a player who tapped "Unirme" on a category match got a toast and
 * a silent `navigate('/editar-perfil')`. The intent was sound — the comment
 * there says leaving somebody on a screen they cannot act on is a dead end — but
 * the landing was a screen IDENTICAL to opening Editar perfil from the Perfil
 * tab: nothing explained why they were there, nothing pointed at "Género
 * (opcional)", and the toast rarely survived the navigation. The persona walk
 * called it "indistinguishable from a broken button", which is exactly right.
 *
 * Asking here costs two taps and never leaves the match. The player keeps their
 * place in the flow, and answers the question at the only moment it decides
 * anything — which is the reason gender was taken OUT of onboarding.
 *
 * It stays optional everywhere else. Refusing to say is a legitimate answer:
 * every Cualquiera match remains open, and that is the vast majority.
 */
export function NeedsGenderSheet({
  open,
  onClose,
  uid,
  category,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  uid: string | null
  /** Drives the copy — a player deserves to know WHICH rule stopped them. */
  category: 'men' | 'women' | 'mixed' | null
  /** Retry the join with the value just saved — never a refetched one. */
  onSaved: (gender: 'male' | 'female') => void
}) {
  const [saving, setSaving] = useState(false)

  const why =
    category === 'women'
      ? 'Este partido es femenino.'
      : category === 'men'
        ? 'Este partido es masculino.'
        : 'Este partido es mixto: dos y dos.'

  async function choose(gender: 'male' | 'female') {
    if (!uid || saving) return
    setSaving(true)
    try {
      await userRepository.saveMine(uid, { padel: { gender } })
      onClose()
      // Retry the join the player already asked for. Saving and then leaving
      // them to tap "Unirme" again would answer the question and abandon the
      // task — and passing the value avoids racing the profile refetch.
      onSaved(gender)
    } catch (err) {
      logger.w('[gender] save failed', err)
      showToast('No pudimos guardarlo. Inténtalo de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="¿Hombre o mujer?">
      <div className="flex flex-col gap-4 pb-2">
        <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
          {why} Solo te lo preguntamos para partidos por categoría — puedes cambiarlo cuando
          quieras en tu perfil.
        </p>
        <div className="flex gap-2">
          <Chip onClick={() => void choose('female')}>Mujer</Chip>
          <Chip onClick={() => void choose('male')}>Hombre</Chip>
        </div>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Ahora no
        </Button>
      </div>
    </BottomSheet>
  )
}
